import React, { useEffect, useMemo, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { cn } from '@/lib/utils';
import { EditorToolbar } from './EditorToolbar';
// COMMENTED OUT: Bubble Menu deactivated
// import { EditorBubbleMenu } from './EditorBubbleMenu';
import { useDirectImagePaste } from '@/hooks/editor/useDirectImagePaste';
import { useEditorExtensionsWithResize } from './config/EditorExtensionsWithResize';

export interface TipTapEditorWithResizeProps {
  content: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  minHeight?: string;
  maxHeight?: string;
  toolbar?: boolean;
  bubbleMenu?: boolean;
  isSaving?: boolean;
  hasError?: boolean;
  workbookId?: string;
  pageId?: string;
  onEditorReady?: (editor: any) => void;
  removeTopRadius?: boolean;
}

/**
 * TipTapEditorWithResize Component
 * 
 * Parallel editor component that includes drag-to-resize image functionality.
 * Maintains the same API as existing TipTap editors for seamless integration.
 * Uses ResizableImage extension instead of standard Image extension.
 */
export const TipTapEditorWithResize = React.memo(function TipTapEditorWithResize({
  content,
  onChange,
  placeholder = 'Start writing...',
  className = '',
  editable = true,
  onFocus,
  onBlur,
  autoFocus = false,
  minHeight = '300px',
  maxHeight,
  toolbar = true,
  bubbleMenu = true,
  isSaving = false,
  hasError = false,
  workbookId,
  pageId,
  onEditorReady,
  removeTopRadius = false,
}: TipTapEditorWithResizeProps) {
  
  // Debug logging for component instantiation
  useEffect(() => {
    console.log('🚀 [TipTapEditorWithResize] Component instantiated!', {
      workbookId,
      pageId,
      contentLength: typeof content === 'string' ? content.length : 0,
      timestamp: new Date().toISOString()
    });
    
    return () => {
      console.log('💀 [TipTapEditorWithResize] Component unmounting');
    };
  }, []);
  
  // Ensure content is always a string
  const safeContent = useMemo(() => {
    return typeof content === 'string' ? content : '';
  }, [content]);

  // Get extensions with resizable image support
  const extensions = useEditorExtensionsWithResize(placeholder);
  
  // Debug logging for extensions
  useEffect(() => {
    console.log('🔧 [TipTapEditorWithResize] Extensions loaded:', {
      extensionCount: extensions.length,
      extensionNames: extensions.map(ext => ext.name),
      timestamp: new Date().toISOString()
    });
  }, [extensions]);

  const editor = useEditor({
    extensions,
    content: safeContent,
    editable,
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        dir: 'ltr',
        style: 'direction: ltr; text-align: left;',
        class: 'resizable-editor', // Add class for scoped CSS
      },
      handlePaste: (view, event) => {
        // Handle image pasting with the same logic as existing editors
        if (workbookId && event.clipboardData?.items) {
          const items = Array.from(event.clipboardData.items);
          const imageItems = items.filter(item => item.type.startsWith('image/'));
          
          if (imageItems.length > 0) {
            const hasFileBasedImages = imageItems.some(item => item.kind === 'file');
            
            if (hasFileBasedImages) {
              console.log('[TipTapEditorWithResize] File-based images detected, preventing TipTap default');
              
              // Emit custom event for component to handle
              const customEvent = new CustomEvent('tiptap-file-paste', {
                detail: { event, workbookId, pageId }
              });
              document.dispatchEvent(customEvent);
              
              return true; // Prevent TipTap's default behavior
            }
          }
        }
        
        return false; // Let TipTap handle other content
      },
      handleDrop: (view, event, slice, moved) => {
        // FIRST: Check if this is internal content movement (repositioning existing content)
        if (moved) {
          console.log('[TipTapEditorWithResize] Internal content movement detected, letting TipTap handle');
          return false; // Let TipTap handle content repositioning with DropCursor
        }

        // THEN: Check for custom image move operations (ResizableImage position dragging)
        if (event.dataTransfer?.types.includes('application/x-tiptap-image')) {
          console.log('[TipTapEditorWithResize] Custom image move operation detected');
          
          try {
            const imageData = JSON.parse(event.dataTransfer.getData('application/x-tiptap-image'));
            const dropCoords = { left: event.clientX, top: event.clientY };
            const dropPos = view.posAtCoords(dropCoords);
            
            if (dropPos) {
              console.log('[TipTapEditorWithResize] Moving image to position:', dropPos.pos);
              
              // Find and delete the original image node
              const { doc } = view.state;
              let originalPos = -1;
              
              doc.descendants((node, pos) => {
                if (node.type.name === 'resizableImage' && node.attrs.src === imageData.src) {
                  originalPos = pos;
                  return false; // Stop searching
                }
                return true;
              });
              
              if (originalPos >= 0) {
                // Create transaction to move the image
                const tr = view.state.tr;
                
                // Delete original
                tr.delete(originalPos, originalPos + 1);
                
                // Adjust drop position if it's after the deleted node
                const adjustedPos = dropPos.pos > originalPos ? dropPos.pos - 1 : dropPos.pos;
                
                // Insert at new position
                const imageNode = view.state.schema.nodes.resizableImage.create(imageData);
                tr.insert(adjustedPos, imageNode);
                
                // Apply transaction
                view.dispatch(tr);
                
                console.log('[TipTapEditorWithResize] Image successfully moved from', originalPos, 'to', adjustedPos);
                return true; // Handled successfully
              }
            }
          } catch (error) {
            console.error('[TipTapEditorWithResize] Error handling image move:', error);
          }
          
          return true; // Prevent default even if error occurred
        }

        // THEN: Check for external file drops (our upload pipeline)
        if (event.dataTransfer?.files) {
          const files = Array.from(event.dataTransfer.files);
          const imageFiles = files.filter(file => file.type.startsWith('image/'));
          
          if (imageFiles.length > 0) {
            console.log('[TipTapEditorWithResize] External image files detected in drop, preventing TipTap/browser default');
            
            // Calculate actual drop position from coordinates
            const dropCoords = { left: event.clientX, top: event.clientY };
            const dropPos = view.posAtCoords(dropCoords);
            console.log('[TipTapEditorWithResize] Drop position calculated:', { dropCoords, dropPos: dropPos?.pos });
            
            // Emit custom event for component to handle
            const customEvent = new CustomEvent('tiptap-file-drop', {
              detail: { 
                event, 
                imageFiles, 
                workbookId, // Can be undefined for private docs
                pageId,
                dropPosition: dropPos?.pos // Include actual drop position
              }
            });
            document.dispatchEvent(customEvent);
            
            return true; // Prevent TipTap's default behavior and browser navigation
          }
        }
        
        // Let TipTap handle all other content (text, other files, etc.)
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML();
      if (onChange) {
        onChange(newContent);
      }
    },
    onFocus: () => {
      onFocus?.();
    },
    onBlur: () => {
      onBlur?.();
    },
    onCreate: ({ editor }) => {
      console.log('🎉 [TipTapEditorWithResize] Editor created, converting existing images to resizable');
      convertStandardImagesToResizable(editor);
    },
  });

  // Function to convert existing standard images to resizable images
  const convertStandardImagesToResizable = useCallback((editor: any) => {
    if (!editor) return;

    let hasConversions = false;
    const tr = editor.state.tr;

    // Traverse the document and find standard image nodes
    editor.state.doc.descendants((node: any, pos: number) => {
      if (node.type.name === 'image') {
        console.log('🔄 [TipTapEditorWithResize] Converting standard image to resizable:', {
          src: node.attrs.src,
          alt: node.attrs.alt,
          position: pos
        });

        // Create a resizable image node with the same attributes
        const resizableImageNode = editor.schema.nodes.resizableImage.create({
          src: node.attrs.src,
          alt: node.attrs.alt,
          title: node.attrs.title,
          // Don't set width/height initially - let the NodeView handle sizing
        });

        // Replace the standard image with resizable image
        tr.replaceWith(pos, pos + node.nodeSize, resizableImageNode);
        hasConversions = true;
      }
    });

    if (hasConversions) {
      console.log('✅ [TipTapEditorWithResize] Applied image conversions to document');
      editor.view.dispatch(tr);
    } else {
      console.log('ℹ️ [TipTapEditorWithResize] No standard images found to convert');
    }
  }, []);

  // Update content when prop changes
  useEffect(() => {
    if (editor && safeContent !== editor.getHTML()) {
      editor.commands.setContent(safeContent);
    }
  }, [safeContent, editor]);

  // Call onEditorReady when editor is available
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Initialize direct image paste functionality
  const { detectAndHandleImagePaste, handleDrop, handlePasteImages } = useDirectImagePaste({
    editor,
    workbookId,
    pageId,
    enabled: editable // Remove !!workbookId requirement to support private contexts
  });

  // Listen for custom file paste events
  useEffect(() => {
    if (!editor || !editable) return; // Remove !workbookId requirement to support private contexts

    const handleFilePasteEvent = (event: CustomEvent) => {
      const { event: clipboardEvent, workbookId: eventWorkbookId, pageId: eventPageId } = event.detail;
      
      // Handle event for our context (both project and private docs)
      // For private docs: workbookId is undefined, so we check if both are undefined
      // For projects: workbookId matches, so we check if they're equal
      const isOurContext = (workbookId === undefined && eventWorkbookId === undefined) || 
                          (workbookId === eventWorkbookId && pageId === eventPageId);
      
      if (isOurContext) {
        console.log('[TipTapEditorWithResize] Handling custom file paste event');
        detectAndHandleImagePaste(clipboardEvent);
      }
    };

    const handleFileDropEvent = (event: CustomEvent) => {
      const { imageFiles, workbookId: eventWorkbookId, pageId: eventPageId, dropPosition } = event.detail;
      
      // Handle event for our context (both project and private docs)
      // For private docs: workbookId is undefined, so we check if both are undefined
      // For projects: workbookId matches, so we check if they're equal
      const isOurContext = (workbookId === undefined && eventWorkbookId === undefined) || 
                          (workbookId === eventWorkbookId && pageId === eventPageId);
      
      if (isOurContext) {
        console.log('[TipTapEditorWithResize] Handling custom file drop event with position:', dropPosition);
        // Use our existing drop handler with the files and drop position
        handleDrop({ dataTransfer: { files: imageFiles } } as DragEvent, dropPosition);
      }
    };

    document.addEventListener('tiptap-file-paste', handleFilePasteEvent as EventListener);
    document.addEventListener('tiptap-file-drop', handleFileDropEvent as EventListener);
    
    return () => {
      document.removeEventListener('tiptap-file-paste', handleFilePasteEvent as EventListener);
      document.removeEventListener('tiptap-file-drop', handleFileDropEvent as EventListener);
    };
  }, [editor, workbookId, pageId, editable, detectAndHandleImagePaste, handleDrop, handlePasteImages]);

  // Handle container click to focus editor
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (!editor || !editable) return;
    
    // Check if click is on the container itself (not content)
    const target = e.target as HTMLElement;
    const proseMirror = target.closest('.ProseMirror');
    
    // If clicking outside the ProseMirror element, focus the editor
    if (!proseMirror) {
      e.preventDefault();
      editor.commands.focus('end');
    }
  }, [editor, editable]);

  return (
    <div className={cn(
      "relative overflow-hidden flex flex-col",
      "bg-white/60 dark:bg-slate-900/50",
      removeTopRadius ? "rounded-b-lg" : "rounded-lg",
      className
    )}
    style={{
      height: '100%', // Take full height of parent
      backdropFilter: 'blur(8px)'
    }}>
      {toolbar && editor && (
        <EditorToolbar 
          editor={editor} 
          workbookId={workbookId} 
          pageId={pageId} 
        />
      )}
      {/* COMMENTED OUT: Bubble Menu deactivated */}
      {/* {bubbleMenu && editor && (
        <EditorBubbleMenu 
          editor={editor} 
          workbookId={workbookId} 
          pageId={pageId} 
        />
      )} */}
      
      {/* Editor content container - handles scrolling and click-to-focus */}
      <div 
        className={cn(
          "flex-1 overflow-y-auto cursor-text",
          // Custom scrollbar styling
          "scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600",
          "scrollbar-track-transparent hover:scrollbar-thumb-gray-500 dark:hover:scrollbar-thumb-gray-500"
        )}
        onClick={handleContainerClick}
        style={{
          minHeight: editable ? minHeight : 'auto',
          maxHeight: maxHeight || 'none'
        }}
      >
        <EditorContent 
          editor={editor} 
          className={cn(
            "prose dark:prose-invert max-w-none focus:outline-none",
            "resizable-editor", // Keep for resizable image CSS
            editable ? "min-h-full" : ""
          )}
          style={{ 
            minHeight: editable ? '100%' : 'auto',
            direction: 'ltr',
            textAlign: 'left'
          }}
        />
      </div>
    </div>
  );
}); 